import * as React from "react"
import {useEffect, useMemo, useRef, useState} from "react"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TableContainer,
  Typography,
  useTheme,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"

import Model, {ModelSetting} from "../../../../model/Model"
import {MilestoneId} from "../../../../model/Milestone"
import Plan from "../../../../model/Plan"
import IModelItem from "../../../../model/IModelItem"
import {useResources} from "../../../../stores/ResourceProvider"
import TextFieldValidator from "../../../../components/form/TextFieldValidator"
import MilestoneDateFieldValidator from "../../../../components/form/MilestoneDateFieldValidator"
import FormValidator from "../../../../components/form/FormValidator"
import ViewMenu from "../../../../components/controls/ViewMenu"
import {
  getISODateFromDate,
  getISODateTime, getISOYear,
  isoToLocalDate,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat,
  percentToNumberFormat,
} from "../../../../stores/StoreUtilities"
import {
  attachSpendToTheEndPhases,
  FRONT_LOADED_REAL_DECLINE,
  ISpendToTheEndResult,
  SMILE_EARLY_MULTIPLIER,
  SMILE_LATE_MULTIPLIER,
  SMILE_MID_MULTIPLIER,
  SolverAbortError,
  solveSpendToTheEnd,
  SpendingCurve,
} from "../../../../components/calculator/SpendToTheEndSolver"
import {FutureCalc} from "../../../../components/calculator/Calculator"
import SpendingBreakdownChart from "./SpendingBreakdownChart"
import FutureViewChart from "./FutureViewChart"
import SpendToTheEndDisclaimer from "./SpendToTheEndDisclaimer"
import Tracking from "../../../../components/Tracking"
import ProjectionErrorAlert from "../../ProjectionErrorAlert";

enum SpendingChartSubView {
  Breakdown,
  LiquidNetWorth,
}

const SpendingChartSubViewNames = ["Spending Breakdown", "Liquid Net Worth"]

interface ISpendToTheEndSettings {
  curve: SpendingCurve
  targetResidual: number
  startDate?: string
  endDate?: string
  inflationRate: number
  disclaimerExpanded: boolean
  chartSubView: SpendingChartSubView
  // Advisor-tunable curve variables (Advanced card). Optional so pre-feature
  // scenarios load the constant defaults.
  frontLoadedRealDecline?: number
  smileEarlyMultiplier?: number
  smileMidMultiplier?: number
  smileLateMultiplier?: number
}

// Tiny inline glyphs (28x16) that sketch each spending curve. They take a color
// prop so they can track the theme's primary color.
const glyphProps = {width: 28, height: 16, viewBox: "0 0 28 16", fill: "none"} as const

const LinearGlyph = ({color}: {color: string}) => (
  <svg {...glyphProps}><path d="M2 8 H26" stroke={color} strokeWidth={2} strokeLinecap="round"/></svg>
)

const FrontLoadedGlyph = ({color}: {color: string}) => (
  <svg {...glyphProps}><path d="M2 3 L26 13" stroke={color} strokeWidth={2} strokeLinecap="round"/></svg>
)

const SmileGlyph = ({color}: {color: string}) => (
  <svg {...glyphProps}><path d="M2 4 Q14 18 26 4" stroke={color} strokeWidth={2} strokeLinecap="round"/></svg>
)

const curveOptions: { value: SpendingCurve; label: string; description: string; Glyph: React.FC<{color: string}> }[] = [
  {
    value: SpendingCurve.Linear,
    label: "Linear (flat real $)",
    description: "Spend the same amount every year in today's dollars, adjusted for inflation. Simple and steady.",
    Glyph: LinearGlyph,
  },
  {
    value: SpendingCurve.FrontLoaded,
    label: "Front-loaded (declining)",
    description: "Maximizes your budget while you are most active, followed by a planned 3% annual decrease in your real purchasing power. Your future dollar amounts will look flat on the chart, but they will buy less each year.",
    Glyph: FrontLoadedGlyph,
  },
  {
    value: SpendingCurve.Smile,
    label: "Retirement smile",
    description: "Splits retirement into three equal phases. Your extra budget dips by ~15% during the middle \"slow-go\" years before rising back up in the final phase to cover late-life expenses.",
    Glyph: SmileGlyph,
  },
]

const SpendToTheEndView = ({
  model,
  plan,
  updatedAt,
  onChange,
}: {
  model: Model
  plan: Plan
  updatedAt: string
  onChange?(item: IModelItem): void
}) => {
  const theme = useTheme()
  const {modelStore, calculator, notify} = useResources()

  const person1 = model.persons?.[0]
  const modelInflationRate = model.inflationStrategy?.expense?.rate ?? Model.defaultInflationRate

  // Form state (loaded from model.settings; defaults below).
  const [curve, setCurve] = useState<SpendingCurve>(SpendingCurve.Linear)
  const [targetText, setTargetText] = useState<string>("$0")
  const [startDate, setStartDate] = useState<string | undefined>(undefined)
  const [endDate, setEndDate] = useState<string | undefined>(undefined)
  const [inflationText, setInflationText] = useState<string>(numberToPercentFormat(modelInflationRate, 1))

  // Result state.
  const [isCalculating, setIsCalculating] = useState<boolean>(false)
  const [solverResult, setSolverResult] = useState<ISpendToTheEndResult | undefined>()
  const [futureCalc, setFutureCalc] = useState<FutureCalc | undefined>()
  const [calcError, setCalcError] = useState<string | undefined>()

  // UI state. Default to the Spending Breakdown chart; the user's last
  // selection is restored from settings on load.
  const [chartSubView, setChartSubView] = useState<SpendingChartSubView>(SpendingChartSubView.Breakdown)
  const [disclaimerExpanded, setDisclaimerExpanded] = useState<boolean>(true)

  // Advanced card: collapsed by default; per-curve variable fields (percent text).
  const [advancedExpanded, setAdvancedExpanded] = useState<boolean>(false)
  const [declineText, setDeclineText] = useState<string>(numberToPercentFormat(FRONT_LOADED_REAL_DECLINE, 1))
  const [smileEarlyText, setSmileEarlyText] = useState<string>(numberToPercentFormat(SMILE_EARLY_MULTIPLIER, 2))
  const [smileMidText, setSmileMidText] = useState<string>(numberToPercentFormat(SMILE_MID_MULTIPLIER, 2))
  const [smileLateText, setSmileLateText] = useState<string>(numberToPercentFormat(SMILE_LATE_MULTIPLIER, 2))

  // Abort + loop-guard refs.
  const abortControllerRef = useRef<AbortController | null>(null)
  // The most recent recalc key (see calcKeyFor) handleCalculate has run for.
  // Prevents the auto-calc effect from re-firing for a model save we triggered
  // ourselves (persistSettings / disclaimer toggle), which bumps only the model
  // updatedAt.
  const lastSeenCalcKeyRef = useRef<string | undefined>(undefined)
  // Skip the first auto-calc until form state has been loaded from settings.
  // Must be state (not a ref) so the auto-calc effect waits for the render
  // after the load — otherwise handleCalculate runs with stale-closure
  // defaults and persistSettings clobbers the saved values.
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false)

  // Resolve a milestone-id or ISO date to a concrete year for the solver.
  const resolveYear = (raw?: string, fallback?: number): number => {
    const fb = fallback ?? new Date().getFullYear()
    if (!raw) return fb
    const milestone = model.milestones?.get(raw as any)
    if (milestone?.date) return getISOYear(milestone.date)
    try {
      return getISOYear(raw)
    } catch {
      return fb
    }
  }

  const resolvedStartYear = useMemo(() => resolveYear(startDate), [startDate, model])
  const resolvedEndYear = useMemo(
    () => resolveYear(endDate, person1?.latestLifeExpectancyYear),
    [endDate, model, person1]
  )

  // Recalc signature: the applied model's updatedAt plus the plan's updatedAt.
  // A non-committed strategy edit (e.g. life expectancy) rebuilds the applied
  // model and bumps the PLAN's updatedAt but not the model's, so keying on the
  // model updatedAt alone misses it. Folding in plan.updatedAt makes strategy
  // edits recalc while still ignoring our own settings saves (model-only bump).
  const calcKeyFor = (modelUpdatedAt?: string) => `${modelUpdatedAt ?? ""}|${plan?.updatedAt ?? ""}`

  // Helper text under a date field: the selected milestone's label when the
  // value is a milestone reference, otherwise the person's age at that date.
  const dateFieldHelperText = (raw: string | undefined, resolvedYear: number): string => {
    if (!person1) return ""
    const milestone = raw ? model.findMilestone(raw) : undefined
    return milestone ? milestone.label : `${person1.nickname} age ${resolvedYear - person1.birthYear}`
  }

  // Clears the chart so the user has to recalculate after editing a setting.
  // Aborts any in-flight solve so the spinner comes down immediately.
  const invalidateChart = () => {
    abortControllerRef.current?.abort()
    setSolverResult(undefined)
    setFutureCalc(undefined)
    setCalcError(undefined)
  }

  // Reset the selected curve's Advanced variables to the research defaults. Like
  // any field edit it only invalidates the chart; the next recalc persists.
  const resetCurveDefaults = () => {
    if (curve === SpendingCurve.FrontLoaded) {
      setDeclineText(numberToPercentFormat(FRONT_LOADED_REAL_DECLINE, 1))
    } else if (curve === SpendingCurve.Smile) {
      setSmileEarlyText(numberToPercentFormat(SMILE_EARLY_MULTIPLIER, 2))
      setSmileMidText(numberToPercentFormat(SMILE_MID_MULTIPLIER, 2))
      setSmileLateText(numberToPercentFormat(SMILE_LATE_MULTIPLIER, 2))
    }
    invalidateChart()
  }

  // Persist the disclaimer's collapsed/expanded state without triggering a
  // recalc — update the lastSeen ref so the save-bumped updatedAt is skipped.
  // Reads the persisted settings (not form state) so unsaved form edits
  // aren't accidentally promoted by a disclaimer toggle.
  const handleToggleDisclaimer = async () => {
    const next = !disclaimerExpanded
    setDisclaimerExpanded(next)
    const current: Partial<ISpendToTheEndSettings> = model.getSetting(ModelSetting.SpendToEnd, {}) ?? {}
    model.setSetting(ModelSetting.SpendToEnd, {...current, disclaimerExpanded: next})
    const updated = await modelStore.saveSettings(model)
    if (updated) {
      if (updated.updatedAt) {
        lastSeenCalcKeyRef.current = calcKeyFor(updated.updatedAt)
      }
      onChange?.(updated)
    }
  }

  // Persist the selected chart without triggering a recalc — same approach as
  // handleToggleDisclaimer: merge into the persisted settings and bump the
  // lastSeen ref so the save-bumped updatedAt is skipped by the auto-calc.
  const handleChartChange = async (index: number) => {
    setChartSubView(index)
    const current: Partial<ISpendToTheEndSettings> = model.getSetting(ModelSetting.SpendToEnd, {}) ?? {}
    model.setSetting(ModelSetting.SpendToEnd, {...current, chartSubView: index})
    const updated = await modelStore.saveSettings(model)
    if (updated) {
      if (updated.updatedAt) {
        lastSeenCalcKeyRef.current = calcKeyFor(updated.updatedAt)
      }
      onChange?.(updated)
    }
  }

  // Load settings from model.settings whenever the model identity or its
  // updatedAt changes. Defaults: Linear curve, $0 target, retire-date or
  // current year as start, latest-life-expectancy milestone as end, model's
  // expense inflation rate.
  useEffect(() => {
    const s: Partial<ISpendToTheEndSettings> = model.getSetting(ModelSetting.SpendToEnd, {}) ?? {}
    setCurve(s.curve ?? SpendingCurve.Linear)
    setTargetText(numberToMoneyFormat(s.targetResidual ?? 0, 0))
    setStartDate(s.startDate ?? defaultStartDate(person1))
    setEndDate(s.endDate ?? defaultEndDate(person1))
    setInflationText(numberToPercentFormat(s.inflationRate ?? modelInflationRate, 1))
    setDisclaimerExpanded(s.disclaimerExpanded ?? true)
    setChartSubView(s.chartSubView ?? SpendingChartSubView.Breakdown)
    setDeclineText(numberToPercentFormat(s.frontLoadedRealDecline ?? FRONT_LOADED_REAL_DECLINE, 1))
    setSmileEarlyText(numberToPercentFormat(s.smileEarlyMultiplier ?? SMILE_EARLY_MULTIPLIER, 2))
    setSmileMidText(numberToPercentFormat(s.smileMidMultiplier ?? SMILE_MID_MULTIPLIER, 2))
    setSmileLateText(numberToPercentFormat(s.smileLateMultiplier ?? SMILE_LATE_MULTIPLIER, 2))
    setSettingsLoaded(true)
  }, [model, updatedAt, person1, modelInflationRate])

  // Auto-calc on mount and on any external edit. Keys on calcKey (model + plan
  // updatedAt) so a non-committed strategy edit — which bumps the plan's
  // updatedAt but not the model's — still triggers a recalc. Records the last
  // key we ran for so our own saveSettings-bumped model updatedAt doesn't
  // re-trigger the calc (handleCalculate writes the post-save key back into the
  // ref so the resulting prop change skips).
  useEffect(() => {
    if (!settingsLoaded || !person1) return
    const calcKey = calcKeyFor(updatedAt)
    if (lastSeenCalcKeyRef.current === calcKey) return
    lastSeenCalcKeyRef.current = calcKey
    handleCalculate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt, plan?.updatedAt, person1, settingsLoaded])

  // On unmount: abort in-flight solver and bust the Calculator singleton's
  // cache so other views don't see results computed against our cloned model.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      calculator.lastFutureCalc = undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCalculate = async () => {
    if (!person1) {
      notify.show("error", "Add a primary person to your model before using Spend to the End.")
      return
    }
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    setIsCalculating(true)

    // R1: Run the solver against an APPLIED clone so other plan changes
    // (Withdrawal, Growth, Conversion, Timeline, etc.) are reflected.
    // R5: cloneModel rebuilds from raw item data — safe vs circular refs.
    const modelCopy = modelStore.cloneModel(model.id)
    if (!modelCopy) {
      notify.show("error", "Unable to clone model for preview.")
      setIsCalculating(false)
      return
    }
    const planCopy = modelCopy.findPlan(plan.id)
    if (!planCopy) {
      notify.show("error", "Unable to find plan in cloned model.")
      setIsCalculating(false)
      return
    }
    planCopy.applyChanges(modelCopy)
    modelCopy.updatedAt = getISODateTime()

    // R2: Defend the Calculator singleton cache from the solver's per-iteration
    // busts so other views' cached projections aren't disturbed.
    const savedCache = calculator.lastFutureCalc
    calculator.lastFutureCalc = undefined

    try {
      const target = moneyToNumberFormat(targetText, 0)
      const inflationRate = percentToNumberFormat(inflationText)
      const result = await solveSpendToTheEnd({
        model: modelCopy,
        plan: planCopy,
        calculator,
        targetResidualToday: target,
        curve,
        startYear: resolvedStartYear,
        endYear: resolvedEndYear,
        inflationRate,
        frontLoadedRealDecline: percentToNumberFormat(declineText),
        smileEarlyMultiplier: percentToNumberFormat(smileEarlyText),
        smileMidMultiplier: percentToNumberFormat(smileMidText),
        smileLateMultiplier: percentToNumberFormat(smileLateText),
        signal: controller.signal,
      })
      if (controller.signal.aborted) return
      setSolverResult(result)

      // Snapshot a FutureCalc with the solved phases attached for the chart.
      // The futureCalc object is independent of the model once returned,
      // so detaching immediately is safe.
      const detach = attachSpendToTheEndPhases(modelCopy, result.solvedPhases)
      try {
        calculator.lastFutureCalc = undefined
        const preview = calculator.calculateFuture(modelCopy, "Spend to the End")
        // The solved plan's preview must calculate too; otherwise show its error instead of a result. Every completed
        // preview sets the error, so a successful retry clears an earlier one
        setCalcError(preview.calcError)
        if (preview.calcError) {
          setSolverResult(undefined)
        }
        setFutureCalc(preview.calcError ? undefined : preview)
      } finally {
        detach()
      }

      Tracking.event({action: "Spend to the End Calculated"})

      const updated = await persistSettings()
      // Record the post-save key so the auto-calc effect skips when the bumped
      // model updatedAt propagates back through the parent as a new prop.
      if (updated?.updatedAt) {
        lastSeenCalcKeyRef.current = calcKeyFor(updated.updatedAt)
      }
    } catch (err: any) {
      if (err instanceof SolverAbortError || err?.name === "AbortError") return
      // A failed solve clears any earlier result, so nothing stale looks current
      setSolverResult(undefined)
      setFutureCalc(undefined)
      setCalcError(err?.message ?? "Calculation failed.")
      notify.show("error", err?.message ?? "Calculation failed.")
    } finally {
      calculator.lastFutureCalc = savedCache
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
        setIsCalculating(false)
      }
    }
  }

  const persistSettings = async (): Promise<Model | undefined> => {
    const settings: ISpendToTheEndSettings = {
      curve,
      targetResidual: moneyToNumberFormat(targetText, 0),
      startDate,
      endDate,
      inflationRate: percentToNumberFormat(inflationText),
      disclaimerExpanded,
      chartSubView,
      frontLoadedRealDecline: percentToNumberFormat(declineText),
      smileEarlyMultiplier: percentToNumberFormat(smileEarlyText),
      smileMidMultiplier: percentToNumberFormat(smileMidText),
      smileLateMultiplier: percentToNumberFormat(smileLateText),
    }
    model.setSetting(ModelSetting.SpendToEnd, settings)
    const update = await modelStore.saveSettings(model)
    if (update) onChange?.(update)
    return update
  }

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} pl={0} mb={1} mr={1}>
      <SpendToTheEndDisclaimer expanded={disclaimerExpanded} onToggle={handleToggleDisclaimer}/>

      <Box mt={0.5} mb={1}>
        <ViewMenu
          view={chartSubView}
          variant="auto"
          size="small"
          options={SpendingChartSubViewNames}
          onChange={(index: number) => handleChartChange(index)}
        />
      </Box>

      <TableContainer component={Paper} sx={{paddingTop: 1, overflowY: "hidden"}}>
        <Box sx={{minHeight: 400, display: "flex", flexDirection: "column", p: 1}}>
          {isCalculating ? (
            <Box sx={placeholderSx(theme)}>
              <CircularProgress size={48}/>
              <Typography variant="body2" color="text.secondary">
                Solving for the optimal year-by-year spending…
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This can take a few seconds.
              </Typography>
            </Box>
          ) : !solverResult ? (
            <Box sx={placeholderSx(theme)}>
              <Typography variant="body2" color="text.secondary">
                Click Recalculate to see your spending projection.
              </Typography>
              <Button variant="contained" color="secondary" onClick={handleCalculate}>
                Recalculate
              </Button>
            </Box>
          ) : !solverResult.feasible ? (
            <Box sx={placeholderSx(theme)}>
              <Alert severity="warning" sx={{maxWidth: 600}}>
                {solverResult.warning ?? "Your plan does not have enough surplus to sustain extra spending."}
              </Alert>
              <Button variant="contained" color="secondary" onClick={handleCalculate}>
                Recalculate
              </Button>
            </Box>
          ) : (
            <>
              {solverResult.warning && (
                <Alert severity="info" sx={{mb: 1}}>{solverResult.warning}</Alert>
              )}
              {futureCalc && chartSubView === SpendingChartSubView.Breakdown && (
                <SpendingBreakdownChart futureCalc={futureCalc} inflationRate={solverResult.inflationRate}/>
              )}
              {futureCalc && chartSubView === SpendingChartSubView.LiquidNetWorth && (
                <FutureViewChart futureCalcs={[futureCalc]} year={0} height={400}/>
              )}
            </>
          )}
        </Box>
      </TableContainer>

      <FormValidator name="spendToTheEndForm">
        <Box sx={{display: "flex", flexDirection: "row", gap: 2, alignItems: "flex-start", mt: 2, flexWrap: "wrap"}}>
          <FormControl variant="standard" margin="dense" sx={{minWidth: 260, mt:"3px"}}>
            <InputLabel margin="dense">Curve Shape</InputLabel>
            <Select
              value={curve}
              label="Curve Shape"
              onChange={(e: any) => { setCurve(e.target.value as SpendingCurve); invalidateChart() }}
              renderValue={(value) => {
                const opt = curveOptions.find(o => o.value === value)
                if (!opt) {
                  return null
                }
                const {Glyph} = opt
                return (
                  <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <Glyph color={theme.palette.primary.main}/>
                    <span>{opt.label}</span>
                  </Box>
                )
              }}
              MenuProps={{PaperProps: {sx: {maxWidth: 360}}}}>
              {curveOptions.map(opt => {
                const {Glyph} = opt
                return (
                  <MenuItem key={opt.value} value={opt.value}
                            sx={{whiteSpace: "normal", alignItems: "flex-start", py: 1, gap: 1.25}}>
                    <Box sx={{mt: "2px", flexShrink: 0}}>
                      <Glyph color={theme.palette.primary.main}/>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{fontWeight: 500, lineHeight: 1.3}}>{opt.label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{display: "block", lineHeight: 1.3}}>{opt.description}</Typography>
                    </Box>
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>

          <TextFieldValidator
            type="string"
            name="targetResidual"
            variant="standard"
            label="Target Residual (today's $)"
            value={targetText}
            validators={{required: true, minMoney: 0, isMoney: true}}
            InputLabelProps={{shrink: true}}
            styleProp={{width: 170, mt:"3px"}}
            onChange={(e: any) => { setTargetText(e.target.value); invalidateChart() }}
          />

          <TextFieldValidator
            type="string"
            name="inflationRate"
            variant="standard"
            label="Inflation"
            value={inflationText}
            validators={{required: true, isPercent: true}}
            InputLabelProps={{shrink: true}}
            styleProp={{width: 120, mt:"3px"}}
            onChange={(e: any) => { setInflationText(e.target.value); invalidateChart() }}
          />

          <MilestoneDateFieldValidator
            name="startDate"
            label="Start Date"
            model={model}
            value={startDate}
            helperText={dateFieldHelperText(startDate, resolvedStartYear)}
            sx={{width: 160}}
            onChange={(value?: string) => { setStartDate(value); invalidateChart() }}
          />

          <MilestoneDateFieldValidator
            name="endDate"
            label="End Date"
            model={model}
            value={endDate}
            helperText={dateFieldHelperText(endDate, resolvedEndYear)}
            sx={{width: 160}}
            onChange={(value?: string) => { setEndDate(value); invalidateChart() }}
          />

          <Button variant="contained" color="secondary" onClick={handleCalculate} disabled={isCalculating} sx={{mt:2}}>
            Recalculate
          </Button>
          <ProjectionErrorAlert message={calcError}/>
        </Box>

        <Paper
          variant="outlined"
          sx={{mt: 2, width: "fit-content", minWidth: 240, maxWidth: "100%", borderRadius: 1, overflow: "hidden"}}>
          <Box
            onClick={() => setAdvancedExpanded((v) => !v)}
            sx={{
              display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 1,
              cursor: "pointer", userSelect: "none",
              "&:hover": {bgcolor: "action.hover"},
            }}>
            {advancedExpanded
              ? <ExpandLessIcon fontSize="small" sx={{color: "text.secondary"}}/>
              : <ExpandMoreIcon fontSize="small" sx={{color: "text.secondary"}}/>}
            <Typography variant="body2" sx={{fontWeight: 600}}>Advanced Curve Parameters</Typography>
          </Box>
          <Collapse in={advancedExpanded}>
            <Box sx={{px: 2, pb: 2, pt: 1.5, borderTop: 1, borderColor: "divider", display: "flex", flexDirection: "row", gap: 2, flexWrap: "wrap", alignItems: "flex-start"}}>
              {curve === SpendingCurve.Linear && (
                <Typography variant="body2" color="text.secondary">
                  No advanced options for this curve.
                </Typography>
              )}

              {curve === SpendingCurve.FrontLoaded && (
                <TextFieldValidator
                  type="string"
                  name="frontLoadedRealDecline"
                  variant="standard"
                  label="Annual real decline"
                  value={declineText}
                  validators={{required: true, isPercent: true, minValue: 0, maxValue: 25}}
                  InputLabelProps={{shrink: true}}
                  styleProp={{width: 170, mt: "3px"}}
                  helperText="Real (today's $) drop per year"
                  onChange={(e: any) => { setDeclineText(e.target.value); invalidateChart() }}
                />
              )}

              {curve === SpendingCurve.Smile && (
                <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                  <Box sx={{display: "flex", flexDirection: "row", gap: 2, flexWrap: "wrap", alignItems: "flex-start"}}>
                    <TextFieldValidator
                      type="string"
                      name="smileEarlyMultiplier"
                      variant="standard"
                      label="Early phase (% of avg)"
                      value={smileEarlyText}
                      validators={{required: true, isPercent: true, minValue: 1, maxValue: 300}}
                      InputLabelProps={{shrink: true}}
                      styleProp={{width: 160, mt: "3px"}}
                      onChange={(e: any) => { setSmileEarlyText(e.target.value); invalidateChart() }}
                    />
                    <TextFieldValidator
                      type="string"
                      name="smileMidMultiplier"
                      variant="standard"
                      label="Mid phase (% of avg)"
                      value={smileMidText}
                      validators={{required: true, isPercent: true, minValue: 1, maxValue: 300}}
                      InputLabelProps={{shrink: true}}
                      styleProp={{width: 160, mt: "3px"}}
                      onChange={(e: any) => { setSmileMidText(e.target.value); invalidateChart() }}
                    />
                    <TextFieldValidator
                      type="string"
                      name="smileLateMultiplier"
                      variant="standard"
                      label="Late phase (% of avg)"
                      value={smileLateText}
                      validators={{required: true, isPercent: true, minValue: 1, maxValue: 300}}
                      InputLabelProps={{shrink: true}}
                      styleProp={{width: 160, mt: "3px"}}
                      onChange={(e: any) => { setSmileLateText(e.target.value); invalidateChart() }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{maxWidth: 520}}>
                    Relative levels — rescaled so the average across all retirement years equals 100%.
                    The shape matters more than the absolute numbers.
                  </Typography>
                </Box>
              )}

              {curve !== SpendingCurve.Linear && (
                <Button variant="outlined" color="primary" onClick={resetCurveDefaults} sx={{mt: "3px"}}>
                  Reset to defaults
                </Button>
              )}
            </Box>
          </Collapse>
        </Paper>
      </FormValidator>
    </Box>
  )
}

function placeholderSx(theme: any) {
  return {
    flex: 1,
    minHeight: 400,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 1,
    backgroundColor: theme.palette.background.paper,
    p: 2,
  }
}

function defaultStartDate(person?: any): string {
  // Store the retirement milestone id so the start date tracks future edits
  // to the retirement date. Falls back to the start of the current year.
  if (person?.retireDate) return MilestoneId.retirement1
  const now = new Date()
  return getISODateFromDate(new Date(now.getFullYear(), 0, 1))
}

function defaultEndDate(person?: any): string | undefined {
  // Store the latest-life-expectancy milestone id so the end date tracks
  // future edits to life expectancy. Resolved live by resolveYear/getDate.
  if (person?.latestLifeExpectancyDate) return MilestoneId.latestLifeExpectancy
  return undefined
}

export default SpendToTheEndView
